import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SubHeader from 'components/common/SubHeader';
import { getInstructorTours, getMyTravelParticipants } from 'api/travel';
import { getPublicUserKey } from 'utils/userIdentity';
import AddIcon from "@mui/icons-material/Add";
import Badge from "components/common/Badge";
import BottomSheet from 'components/common/BottomSheet';
import 'assets/scss/pages/AdminMyTourList.scss';

const STATUS_TABS = [
    { key: 'ALL', label: '전체' },
    { key: 'ONGOING', label: '진행' },
    { key: 'UPCOMING', label: '예정' },
    { key: 'REJECTED', label: '거절' },
    { key: 'PENDING', label: '대기'}
];

const STATUS_CONFIG = {
    ONGOING: { label: '진행', cardClass: '' },
    UPCOMING: { label: '예정', cardClass: '' },
    REJECTED: { label: '거절', cardClass: 'my-tour-card--ended' },
    PENDING: {label: '대기', cardClass: 'my-tour-card--ended' }
};

const fmtPrice = (n) =>
    n != null ? `₩${Number(n).toLocaleString('ko-KR')}` : '-';

const fmtDate = (d) => (d ? String(d).replace(/-/g, '.') : '');

const AdminMyTourList = () => {
    const navigate = useNavigate();
    const [tours, setTours] = useState([]);
    const [activeTab, setActiveTab] = useState('ALL');
    const [loading, setLoading] = useState(true);

    // 참가자 보기 바텀시트
    const [participantSheetOpen, setParticipantSheetOpen] = useState(false);
    const [participants, setParticipants] = useState([]);
    const [participantsLoading, setParticipantsLoading] = useState(false);
    const [openMenuId, setOpenMenuId] = useState(null);

    useEffect(() => {
        setLoading(true);
        getInstructorTours()
            .then((data) => {
                const list = Array.isArray(data)
                    ? data
                    : Array.isArray(data?.results)
                    ? data.results
                    : [];
                setTours(list);
            })
            .finally(() => setLoading(false));
    }, []);

    const filteredTours =
        activeTab === 'ALL'
            ? tours
            : tours.filter((t) => t.approval_status === 'PENDING' || t.approval_status === 'REJECTED' ?  t.approval_status === activeTab :t.status === activeTab);

    const clickEditBtn = (id) => navigate(`/tour/edit/${id}`);

    const clickDetailBtn = (approve, id) => {
      approve && navigate(`/tour/detail/${id}`)
    };

    // 참가자 보기: 투어 상세 API 호출 후 participants만 추출하여 바텀시트로 표시
    const clickParticipantsBtn = async (id) => {
        setParticipantSheetOpen(true);
        setParticipantsLoading(true);
        setParticipants([]);
        try {
            const data = await getMyTravelParticipants(id);
            setParticipants(Array.isArray(data?.participants) ? data.participants : []);
        } finally {
            setParticipantsLoading(false);
        }
    };


    return (
        <div className="my-tour-list-page">
            <SubHeader title="내 투어 목록" />

            <div className="page-content">
                {/* 상태 필터 탭 */}
                <div className="filter-tabs" role="tablist" aria-label="투어 상태 필터">
                    {STATUS_TABS.map((tab) => (
                        <button
                            key={tab.key}
                            role="tab"
                            aria-selected={activeTab === tab.key}
                            className={`filter-tab${
                                activeTab === tab.key ? ' filter-tab--active' : ''
                            }`}
                            onClick={() => setActiveTab(tab.key)}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* 투어 카드 목록 */}
                {loading ? (
                    <div className="empty-state">불러오는 중...</div>
                ) : filteredTours.length === 0 ? (
                    <div className="empty-state">
                        {activeTab === 'ALL'
                            ? '개설한 투어가 없습니다.'
                            : '해당 상태의 투어가 없습니다.'}
                    </div>
                ) : (
                    <div className="my-tour-card-list">
                        {filteredTours.map((tour) => {
                            const isPendingOrRejected = tour.approval_status  === 'PENDING' || tour.approval_status  === 'REJECTED';
                            const status = isPendingOrRejected ?  tour.approval_status  : tour.status;
                            const config = STATUS_CONFIG[status];
                            const departDate = fmtDate(tour.start_date);
                            return (
                                <div
                                    key={tour.id}
                                    className={`my-tour-card ${config.cardClass}`}
                                    onClick={() => clickDetailBtn(!isPendingOrRejected, tour.id)}
                                    role="button"
                                    tabIndex={0}
                                  
                                    aria-label={`${tour.name} 투어 상세보기`}
                                >
                                    <div
                                        className="tour-hero"
                                        style={
                                            tour.thumbnail_image
                                                ? { backgroundImage: `url(${tour.thumbnail_image})` }
                                                : {}
                                        }
                                        aria-hidden="true"
                                    >
                                        <span className={`status-badge`}>
                                            <Badge text={config.label} type='solid' color={config.label === '대기'  ? 'black':config.label === '거절' ? 'red' : 'purple'}></Badge>
                                        </span>
                                        <div className="hero-texts">
                                            <div className="tour-location">
                                                {tour.location || tour.name}
                                            </div>
                                            <div className="tour-departure">
                                                {departDate && `${departDate} 출발`}
                                            </div>
                                        </div>
                                    </div>

                                    {/* 정보 */}
                                    <div className="tour-info">
                                        <div className="tour-name">{tour.name}</div>

                                        <div className="tour-meta">
                                            {tour.max_participants && (
                                                <div className="meta-item">
                                                    최대 인원{' '}
                                                    <span>{tour.max_participants}명</span>
                                                </div>
                                            )}
                                            {tour.available_spots != null && (
                                                <div className="meta-item">
                                                    신청 <span>{tour.max_participants - tour.available_spots}명</span>
                                                </div>
                                            )}
                                            {tour.start_date && tour.end_date && (
                                                <div className="meta-item">
                                                    일정{' '}
                                                    <span>
                                                        {fmtDate(tour.start_date)} ~{' '}
                                                        {fmtDate(tour.end_date)}
                                                    </span>
                                                </div>
                                            )}
                                            {tour.location && (
                                                <div className="meta-item">
                                                    장소 <span>{tour.location}</span>
                                                </div>
                                            )}
                                        </div>

                                        <div className="tour-bottom">
                                            <div className="tour-price">
                                                예약금 {fmtPrice(tour.price)}
                                            </div>
                                            <div>
                                              {isPendingOrRejected? 
                                              <div className='btn-wrap btn-wrap-flex gap-8 btn-wrap-no-padding'>
                                                <button
                                                className="btn btn-small btn-fit btn-purple400"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    clickEditBtn(tour.id);
                                                }}
                                                aria-label={`${tour.name} 수정`}
                                              >
                                                  수정
                                              </button>
                                              </div>
                                              : 
                                              <div className='btn-wrap btn-wrap-flex gap-8 btn-wrap-no-padding'>     
                                                <button
                                                    className="btn btn-small btn-outline-purple500"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        clickParticipantsBtn(tour.id)
                                                    }}
                                                    aria-label={`${tour.name} 참가자 보기`}
                                                >
                                                    참가자 보기
                                                </button>
                                            </div>}
                                            </div>
                              
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
            <div>
            <button className="btn btn-circle btn-purple500" onClick={() => navigate('/tour/regist')}>
                    <AddIcon />
                  </button>
            </div>

            <BottomSheet
                isOpen={participantSheetOpen}
                onClose={() => { setParticipantSheetOpen(false); setOpenMenuId(null); }}
                title="참가자"
            >
                {participantsLoading ? (
                    <div className="participant-empty">불러오는 중...</div>
                ) : participants.length === 0 ? (
                    <div className="participant-empty">참가자가 없습니다.</div>
                ) : (
                    <ul className="participant-list">
                        {participants.map((p, i) => {
                            const u = p.user || {};
                            const phone = u.hp || '';
                            const companions = Array.isArray(p.companions) ? p.companions : [];
                            const genderLabel = (g) => g === 'M' ? '남' : g === 'F' ? '여' : '-';
                            return (
                                <li key={u.user_id} className="participant-item">
                                    <div className="participant-row">
                                        <div className="participant-info">
                                            <div className="participant-name-row">
                                                <span className="participant-name">{u.name}({genderLabel(p.gender)})</span>
                                                <span style={{textDecoration:'underline'}}><a href={`tel:${phone}`}  aria-label={'전화하기'}>{u.hp}</a></span>
                                            </div>
                                            <div className="participant-info">{u.user_id} | {p.license}</div>
                                        </div>
                                        <div className="participant-actions">
                                            {getPublicUserKey(u) && (
                                                <a
                                                    href={`/community/user/${encodeURIComponent(getPublicUserKey(u))}`}
                                                    className="participant-message-btn"
                                                    aria-label={'커뮤니티 프로필'}
                                                >
                                                    프로필 보기
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                    {companions.length > 0 && (
                                        <ul className="companion-list">
                                            {companions.map((c, ci) => {
                                                const cPhone = c.hp || '';
                                                return (
                                                    <li key={ci} className="companion-item">
                                                        <span className="companion-name">{c.name}({genderLabel(c.gender)})</span>
                                                        {cPhone && (
                                                            <a href={`tel:${cPhone}`} className="companion-phone" aria-label={'전화하기'}>{cPhone}</a>
                                                        )}
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                )}
            </BottomSheet>
        </div>
    );
};

export default AdminMyTourList;
